import { useEffect, useState } from "react";
import { Stethoscope, Pill, Target, Activity } from "lucide-react";
import api from "@/api";

// Référentiels canoniques de traitement : source unique backend (clinical_config.py via /clinical/reference-data).
// Plus aucune liste locale de lignes / types / statuts côté frontend (2026-09-16).
const EMPTY_REFERENTIALS = { therapeuticLines: [], treatmentTypes: [], treatmentModes: [], treatmentStatus: [] };
let referentialsCache = null;
let referentialsPromise = null;

export function loadTreatmentReferentials() {
  if (referentialsCache) return Promise.resolve(referentialsCache);
  if (!referentialsPromise) {
    referentialsPromise = api.get("/clinical/reference-data").then((res) => {
      const d = res.data || {};
      referentialsCache = {
        therapeuticLines: d.therapeuticLines || [],
        treatmentTypes: d.treatmentTypes || [],
        treatmentModes: d.treatmentModes || [],
        treatmentStatus: d.treatmentStatus || [],
      };
      return referentialsCache;
    }).catch(() => { referentialsPromise = null; return EMPTY_REFERENTIALS; });
  }
  return referentialsPromise;
}

export function useTreatmentReferentials() {
  const [refs, setRefs] = useState(referentialsCache || EMPTY_REFERENTIALS);
  useEffect(() => {
    let alive = true;
    loadTreatmentReferentials().then((r) => { if (alive) setRefs(r); });
    return () => { alive = false; };
  }, []);
  return refs;
}

export const CANCER_TYPES = [
  "Sein",
  "Poumon",
  "Poumon non a petites cellules (CBNPC)",
  "Poumon a petites cellules (CPC)",
  "Colon",
  "Rectum",
  "Colorectal",
  "Prostate",
  "Ovaire",
  "Pancreas",
  "Estomac",
  "Foie (Hepatocarcinome)",
  "Rein",
  "Vessie",
  "Thyroide",
  "Tete et cou (ORL)",
  "Oesophage",
  "Col de l'uterus",
  "Endomètre (uterus)",
  "Melanome",
  "Cerveau (Glioblastome)",
  "Sarcome des tissus mous",
  "Osteosarcome",
  "Lymphome Hodgkinien",
  "Lymphome non-Hodgkinien",
  "Leucemie aigue",
  "Leucemie chronique",
  "Myelome multiple",
  "Autre",
];

/**
 * Check if patient has new structured oncology fields
 */
export function hasStructuredOncology(patient) {
  return patient?.cancerType || patient?.treatmentType || patient?.therapeuticLine || patient?.protocol;
}

/**
 * Format cancer display: "Sein (HER2+)" or just "Sein"
 */
export function formatCancerDisplay(patient) {
  if (!patient) return "—";
  
  if (hasStructuredOncology(patient)) {
    const base = patient.cancerType || "";
    const detail = patient.cancerTypeDetail;
    return detail ? `${base} (${detail})` : base || "—";
  }
  
  // Fallback to legacy pathology field
  return patient.pathology || "—";
}

/**
 * Format treatment display
 */
export function formatTreatmentDisplay(patient) {
  if (!patient) return "—";
  
  if (hasStructuredOncology(patient)) {
    return patient.treatmentType || "—";
  }
  
  // Fallback to legacy treatment field
  return patient.treatment || "—";
}

/**
 * Format therapeutic line display
 */
export function formatLineDisplay(patient) {
  if (!patient) return null;
  return patient.therapeuticLine || null;
}

/**
 * Format protocol display
 */
export function formatProtocolDisplay(patient) {
  if (!patient) return null;
  return patient.protocol || null;
}

/**
 * Compact display component for patient lists/tables
 * Shows: Cancer Type | Treatment Type
 */
export function OncologyCompactDisplay({ patient, className = "" }) {
  const cancer = formatCancerDisplay(patient);
  const treatment = formatTreatmentDisplay(patient);
  
  return (
    <div className={`space-y-1 ${className}`}>
      <p className="text-sm text-slate-700">{cancer}</p>
      <p className="text-xs text-slate-500">{treatment}</p>
    </div>
  );
}

/**
 * Full display component for patient detail views
 * Shows all oncology fields in a grid
 */
export function OncologyFullDisplay({ patient, className = "" }) {
  if (!patient) return null;

  const hasNewFields = hasStructuredOncology(patient);
  
  // If patient only has legacy fields, show them in simple format
  if (!hasNewFields && (patient.pathology || patient.treatment)) {
    return (
      <div className={`grid grid-cols-2 gap-4 ${className}`}>
        <div>
          <p className="text-xs text-slate-500">Pathologie</p>
          <p className="font-medium text-slate-800">{patient.pathology || "—"}</p>
        </div>
        <div>
          <p className="text-xs text-slate-500">Traitement</p>
          <p className="font-medium text-slate-800">{patient.treatment || "—"}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`grid grid-cols-2 md:grid-cols-4 gap-4 ${className}`}>
      <div>
        <p className="text-xs text-slate-500 flex items-center gap-1">
          <Target className="w-3 h-3" /> Type de cancer
        </p>
        <p className="font-medium text-slate-800">{formatCancerDisplay(patient)}</p>
      </div>
      <div>
        <p className="text-xs text-slate-500 flex items-center gap-1">
          <Pill className="w-3 h-3" /> Traitement
        </p>
        <p className="font-medium text-slate-800">{formatTreatmentDisplay(patient)}</p>
      </div>
      <div>
        <p className="text-xs text-slate-500 flex items-center gap-1">
          <Activity className="w-3 h-3" /> Ligne
        </p>
        <p className="font-medium text-slate-800">{patient.therapeuticLine || "—"}</p>
      </div>
      <div>
        <p className="text-xs text-slate-500 flex items-center gap-1">
          <Stethoscope className="w-3 h-3" /> Protocole
        </p>
        <p className="font-medium text-slate-800">{patient.protocol || "—"}</p>
      </div>
    </div>
  );
}

/**
 * Info cards for patient header
 */
export function OncologyInfoCards({ patient }) {
  if (!patient) return null;

  const hasNewFields = hasStructuredOncology(patient);
  
  // Legacy format - show 2 cards
  if (!hasNewFields) {
    return (
      <>
        <InfoCard
          icon={Stethoscope}
          label="Pathologie"
          value={patient.pathology || "Non renseignée"}
          color="blue"
        />
        <InfoCard
          icon={Pill}
          label="Traitement"
          value={patient.treatment || "Non renseigné"}
          color="purple"
        />
      </>
    );
  }

  // New format - show 4 cards
  return (
    <>
      <InfoCard
        icon={Target}
        label="Cancer"
        value={formatCancerDisplay(patient)}
        color="blue"
      />
      <InfoCard
        icon={Pill}
        label="Traitement"
        value={formatTreatmentDisplay(patient)}
        color="purple"
      />
      <InfoCard
        icon={Activity}
        label="Ligne"
        value={patient.therapeuticLine || "—"}
        color="amber"
      />
      <InfoCard
        icon={Stethoscope}
        label="Protocole"
        value={patient.protocol || "—"}
        color="teal"
      />
    </>
  );
}

// Internal InfoCard component
function InfoCard({ icon: Icon, label, value, color }) {
  const colorClasses = {
    blue: "bg-blue-50 text-blue-600",
    purple: "bg-purple-50 text-purple-600",
    teal: "bg-teal-50 text-teal-600",
    emerald: "bg-emerald-50 text-emerald-600",
    amber: "bg-amber-50 text-amber-600",
    red: "bg-red-50 text-red-600",
  };

  return (
    <div className="p-3 bg-slate-50 rounded-xl">
      <div className="flex items-center gap-2 mb-1">
        <div className={`p-1.5 rounded-lg ${colorClasses[color]}`}>
          <Icon className="w-3.5 h-3.5" />
        </div>
        <span className="text-xs text-slate-500">{label}</span>
      </div>
      <p className="font-medium text-slate-800 text-sm truncate" title={value}>
        {value}
      </p>
    </div>
  );
}

export default OncologyFullDisplay;
